import ApiConfig from "../api/config/api-config";
import {DateTime} from "luxon";
import {
    SubscriptionLogsApi,
    SubscriptionsApi,
    SubscriptionsController$SubscriptionJson,
    SubscriptionsController$SubscriptionListJson,
    SubscriptionType
} from "../api/generated/herds-licensing";

export type Subscription = SubscriptionsController$SubscriptionJson & {
    cancelledAt: DateTime;
    expiresAt: DateTime;
    validFrom: DateTime;
    isExpired: boolean;
    isActive: boolean;
};

type SubscriptionOptions = {
    duration: number;
    start?: DateTime;
    type?: SubscriptionType;
};

export interface ISubscriptionService {
    active(): Promise<Subscription>;
    list(): Promise<SubscriptionsController$SubscriptionListJson[]>

    subscribeBreeder(breederId: string, opts: SubscriptionOptions): Promise<void>;
    unsubscribeBreeder(breederId: string): Promise<void>;

    subscribeAssociation(registryId: string, opts: SubscriptionOptions): Promise<void>;
    unsubscribeAssociation(registryId: string): Promise<void>;

    history(breederId: string): Promise<string[]>;
}

const convertSubscription = (item: SubscriptionsController$SubscriptionJson): Subscription => {
    const now = DateTime.now();
    const validFrom = DateTime.fromISO(item.validFrom);
    const expiresAt = DateTime.fromISO(item.expiresAt);
    return ({
        ...item,
        validFrom: validFrom,
        expiresAt: expiresAt,
        cancelledAt: DateTime.fromISO(item.cancelledAt),
        isExpired: expiresAt < now,
        isActive: now > validFrom && now < expiresAt,
    });
};

const toPayload = ({duration, start, type}: SubscriptionOptions, fallbackType: SubscriptionType) => ({duration, start, type: type ?? fallbackType});

class SubscriptionServiceImpl implements ISubscriptionService {
    constructor(
        private readonly subscriptionApi: SubscriptionsApi,
        private readonly logsApi: SubscriptionLogsApi) {
    }

    async list(): Promise<SubscriptionsController$SubscriptionListJson[]> {
        const items = (await this.subscriptionApi.listAll()).items
        return items.map(item => ({
            ...item,
            subscription: convertSubscription(item.subscription),
        }));
    }

    async subscribeBreeder(breederId: string, opts: SubscriptionOptions): Promise<void> {
        await this.subscriptionApi.subscribe(breederId, toPayload(opts, "INDIVIDUAL"));
    }

    async unsubscribeBreeder(breederId: string): Promise<void> {
        await this.subscriptionApi.unsubscribe(breederId);
    }

    async subscribeAssociation(registryId: string, opts: SubscriptionOptions): Promise<void> {
        await this.subscriptionApi.subscribeAssociation(registryId, toPayload(opts, "VOLUME"));
    }

    async unsubscribeAssociation(registryId: string): Promise<void> {
        await this.subscriptionApi.unsubscribeAssociation(registryId);
    }

    async active(): Promise<Subscription> {
        return convertSubscription((await this.subscriptionApi.get()).item);
    }

    async history(breederId: string): Promise<string[]> {
        return (await this.logsApi.history(breederId)).items;
    }
}

const subscriptionsApi = new SubscriptionsApi(new ApiConfig());
const subscriptionLogsApi = new SubscriptionLogsApi(new ApiConfig());
export const SubscriptionService: ISubscriptionService = new SubscriptionServiceImpl(subscriptionsApi, subscriptionLogsApi);